/**
 * /payment-defaults - subscriptions in payment default (OCP-846).
 *
 * AGB V4 (OWS-D23) 6.5 lets us bar use during a payment default for at most
 * 6 months; counsel (2026-10-05): a longer default terminates the contract.
 * This page lists every past-due subscription with the date the default
 * began, the 6.5 termination date and the days left. The daily sweep
 * (02:40 UTC) terminates due rows by itself; "Terminate now" is the operator
 * path for a retry after a provider failure or for ending a default early
 * (same REST route as the MCP tool ocp_terminate_payment_default, reason
 * mandatory, audited with the source).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';

export interface PaymentDefaultRow {
  subscription_id: string;
  customer_id: string;
  customer_email: string;
  company_name: string | null;
  product_id: string;
  product_name: string;
  product_family: string;
  billing_provider: 'polar' | 'paddle' | null;
  provider_subscription_id: string | null;
  past_due_since: string | null;
  provider_revoked_at: string | null;
  termination_due_at: string | null;
  days_in_default: number | null;
  days_until_termination: number | null;
}

interface ListResponse {
  max_suspension_months: number;
  clause: string;
  items: PaymentDefaultRow[];
}

export function AdminPaymentDefaultsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api<ListResponse>('/v1/admin/payment-defaults'));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load payment defaults.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onTerminate = useCallback(
    async (row: PaymentDefaultRow) => {
      const due = row.days_until_termination !== null && row.days_until_termination <= 0;
      const reason = prompt(
        due
          ? `Terminate ${row.product_name} of ${row.customer_email} now (AGB V4 6.5, default since ${fmt(row.past_due_since)})? Reason (min 10 characters):`
          : `The 6-month cap is NOT reached yet (due ${fmt(row.termination_due_at)}). Terminate ${row.product_name} of ${row.customer_email} early anyway? Reason (min 10 characters):`,
      );
      if (!reason || reason.trim().length < 10) return;
      setPendingId(row.subscription_id);
      setNotice(null);
      try {
        const r = await api<{ outcome: string; paused_tenants?: unknown[]; notice_recipients?: string[] }>(
          `/v1/admin/payment-defaults/${row.subscription_id}/terminate`,
          { method: 'POST', body: { reason: reason.trim(), ...(due ? {} : { force: true }) } },
        );
        setNotice(
          `Terminated. ${r.paused_tenants?.length ?? 0} workspace(s) entered the 12.3 export window; notice sent to ${(r.notice_recipients ?? []).join(', ') || 'nobody (see audit)'}.`,
        );
        void load();
      } catch (err) {
        const body = err instanceof ApiError ? (err.body as { error?: string; detail?: string } | null) : null;
        setError(body?.error ? `${body.error}${body.detail ? `: ${body.detail}` : ''}` : err instanceof Error ? err.message : 'Termination failed.');
      } finally {
        setPendingId(null);
      }
    },
    [load],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Payment defaults</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Subscriptions in payment default (<code>past_due</code>). AGB V4 6.5: use may be barred for at most{' '}
          {data?.max_suspension_months ?? 6} months; a longer default terminates the contract. The daily sweep
          (02:40 UTC) terminates due rows by itself - the account&apos;s workspaces then enter the 90-day export
          window of 12.3 and the hard-delete queue deletes only after it. Polar revokes a past-due subscription
          itself after 21 days of failed retries; that ends the contract with the same consequences.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="rounded border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 px-3 py-2 text-sm text-green-800 dark:text-green-300">
          {notice}
        </div>
      )}

      {!data && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {data && data.items.length === 0 && (
        <div data-testid="payment-defaults-empty" className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          No subscription is in payment default.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Account</th>
                <th className="px-3 py-2 text-left">Subscription</th>
                <th className="px-3 py-2 text-left">Provider</th>
                <th className="px-3 py-2 text-left">In default since</th>
                <th className="px-3 py-2 text-left">Termination due (6.5)</th>
                <th className="px-3 py-2 text-right">Days left</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {data.items.map((r) => (
                <tr key={r.subscription_id} data-testid={`payment-default-row-${r.subscription_id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/accounts/${r.customer_id}`} className="text-brand-700 underline">
                      {r.company_name ?? r.customer_email}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{r.product_name}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {r.billing_provider ?? 'none'}
                    {r.provider_revoked_at && (
                      <span className="ml-1 text-xs text-amber-700 dark:text-amber-300" data-testid={`provider-revoked-${r.subscription_id}`} title="The provider ended its side after its dunning. Under 6.5 this is not a termination; the subscription stays in default until it is paid or the 6-month sweep ends it.">
                        (revoked by provider {fmt(r.provider_revoked_at)})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {fmt(r.past_due_since)}
                    {r.days_in_default !== null && <span className="ml-1 text-xs text-gray-500">({r.days_in_default} d)</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{fmt(r.termination_due_at)}</td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${r.days_until_termination !== null && r.days_until_termination <= 0 ? 'font-semibold text-red-700 dark:text-red-300' : ''}`}
                  >
                    {r.days_until_termination ?? '-'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => void onTerminate(r)}
                      disabled={pendingId === r.subscription_id}
                      className="text-xs text-red-700 dark:text-red-300 underline disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                      Terminate now
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return 'not recorded';
  try {
    return new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}
